/* ------------------------ Functional imports start ------------------------ */
import { type DetailQueryParameters, type PaginatedQueryParameters, SearchOptions } from "./types";
/* ------------------------- Functional imports end ------------------------- */

export type DetailQueryKey<TEntity> = (string | DetailQueryParameters<TEntity>)[]
export type ListQueryKey<TEntity, TFilters> =  (string | {ids: number[]} | PaginatedQueryParameters<TEntity, TFilters>)[]

/**
 * Factory class for generating query keys for API calls.
 *
 * When using TanStack Query, we need to provide so-called query keys that are used
 * to uniquely identify a query and its cache entry. Using this key, TanStack Query
 * is able to manage an internal cache and provide cached data without fetching it
 * from the backend whenever viable.
 *
 * This class can be used to instantiate query key factories that streamline and
 * simplify the creation of query keys for specific resources.
 *
 * To learn more about query keys, read: https://tanstack.com/query/latest/docs/guides/query-keys
 */
export default class QueryKeyFactory {
  readonly baseKey: string[];

  /* ------------------------ Utility Methods Start ------------------------- */
  /**
   * Validates whether the provided key is a valid string type baseKey.
   * @param baseKey string - The key to validate.
   * @returns boolean - True if the key is valid, false otherwise.
   * @private
   */
  private isValidBaseKeyString(baseKey: string): boolean {
    return baseKey.length > 0;
  }

  /**
   * Validates whether the provided key is a valid array type baseKey.
   * @param baseKey string[] - The baseKey to validate.
   * @returns [boolean, number] - True if the baseKey is valid, false and the index
   *                              of the first invalid entry otherwise.
   *                              An invalid baseKey with an index of -1 means that
   *                              the baseKey is empty.
   * @private
   */
  private isValidBaseKeyArray(baseKey: string[]): [boolean, number] {
    if (baseKey.length === 0) return [false, -1]
    for (let i=0; i<baseKey.length; i++) {
      if (!this.isValidBaseKeyString(baseKey[i])) return [false, i]
    }
    return [true, -1]
  }

  /**
   * Takes a PaginatedQueryParameters or DetailQueryParameters object and returns a
   * deterministic version of it.
   * 
   * We need to make sure that the query parameters are deterministic.
   * That means that the same PaginatedQueryParameters or DetailQueryParameters always
   * result in the same query key, regardless of the order of the parameters or
   * empty values.
   * (E.g. "sort: undefined" and "sort: {}" would semantically describe the same
   * data, but will result in different query keys and therefore different cache
   * entries.)
   * To learn more, read: https://tanstack.com/query/latest/docs/framework/react/guides/query-keys#query-keys-are-hashed-deterministically
   * 
   * @param params PaginatedQueryParameters - The PaginatedQueryParameters to make deterministic.
   * @returns PaginatedQueryParameters - A deterministic version of the PaginatedQueryParameters.
   * @returns DetailQueryParameters - A deterministic version of the DetailQueryParameters.
   * @private
   */
  private getDeterministicQueryParams<TEntity, TFilters>(
    params: DetailQueryParameters<TEntity> | PaginatedQueryParameters<TEntity, TFilters> = {}
  ): DetailQueryParameters<TEntity> | PaginatedQueryParameters<TEntity, TFilters> {
    // typecast params to make TypeScript happy
    const castedParams = params as Partial<PaginatedQueryParameters<TEntity, TFilters>>

    // we start with an empty object, to only add parameters that actually have a value
    const deterministicParams: Partial<PaginatedQueryParameters<TEntity, TFilters>> = {}

    // begin with the detail parameters
    if(castedParams.filters && Object.keys(castedParams.filters).length > 0) {
      deterministicParams.filters = castedParams.filters
      if(typeof castedParams.filterChainingOperator === 'undefined') {
        // fallback to default
        deterministicParams.filterChainingOperator = 'AND'
      } else {
        deterministicParams.filterChainingOperator = castedParams.filterChainingOperator
      }
    }
    if(castedParams.expand) deterministicParams.expand = castedParams.expand.sort()
    if(castedParams.only) deterministicParams.only = castedParams.only.sort()

    if(Object.hasOwn(castedParams, 'sort')
       // || Object.hasOwn(castedParams, 'ids')
       || Object.hasOwn(castedParams, 'pageSize')
       || Object.hasOwn(castedParams, 'page')
    ) {
      // the provided parameters are of type PaginatedQueryParameters
      // -> continue with the list parameters
      if(castedParams.sort) deterministicParams.sort = castedParams.sort
      // if(castedParams.ids) deterministicParams.ids = castedParams.ids.sort()
      if(castedParams.pageSize) deterministicParams.pageSize = castedParams.pageSize
      if(castedParams.page) deterministicParams.page = castedParams.page

      return deterministicParams as PaginatedQueryParameters<TEntity, TFilters>
    }
    return deterministicParams as DetailQueryParameters<TEntity>
  }
  /* ------------------------- Utility Methods End -------------------------- */

  /**
   * Constructor for QueryKeyFactory. Use this to create a query key factory for
   * a specific resource (where resource means e.g., Articles, Contracts, ...).
   * @param baseKey string[] - The base cache-key for the resource.
   */
  constructor(baseKey: string | string[]) {
    if (typeof baseKey === 'string') {
      if (!this.isValidBaseKeyString(baseKey)) {
        throw new Error(
          'The empty string is not a valid baseKey. Make sure to provide a non-empty ' +
          'string or an array of strings that uniquely identify the QueryKeyFactory.'
        )
      }
      this.baseKey = [baseKey]
    }
    else {
      const [isValid, index] = this.isValidBaseKeyArray(baseKey)
      if (!isValid && index === -1) {
        throw new Error(
          'The empty array is not a valid baseKey. Make sure to provide a non-empty ' +
          'string or an array of strings that uniquely identify the QueryKeyFactory.'
        )
      }
      if (!isValid && index >= -1) {
        throw new Error(
          `The baseKey contains an invalid key string at index ${index}.` +
          'Make sure to provide only non-empty key strings that uniquely identifies' +
          'the QueryKeyFactory.'
        )
      }
      this.baseKey = baseKey
    }
  }

  /**
   * Returns the base cache-key for the resource. Use this key if you want to
   * invalidate the entire cache for the resource.
   * @returns string[] - The base cache-key of the QueryKeyFactory.
   */
  get all(): readonly string[] { return this.baseKey }

  /**
   * Returns the base cache-key for all queries that return a paginated response
   * containing a list of data. Use this key if you want to invalidate the cache
   * for all list queries.
   * @returns string[] - The base cache-key for list keys of the QueryKeyFactory.
   */
  lists(): string[] { return [...this.all, 'list'] as const }

  /**
   * Returns a specific list cache-key described by the provided PaginatedQueryParameters.
   * @param params PaginatedQueryParameters - The parameters to uniquely describe the list query.
   * @param ids number[] - Optional. If provided, the list query will be specific to the provided ids.
   * @returns string[] - The cache-key for the list query.
   */
  list<TEntity, TFilters>(
    params: PaginatedQueryParameters<TEntity, TFilters> = {},
    ids?: number[]
  ): ListQueryKey<TEntity, TFilters> {
    if(ids && ids.length > 0) {
      return [
        ...this.lists(),
        // include a deterministic array of the provided ids
        {ids: ids.sort()},
        this.getDeterministicQueryParams(params)
      ] as const
    }
    return [...this.lists(), this.getDeterministicQueryParams(params)] as const
  }

  /**
   * Returns a specific search cache-key described by the provided PaginatedQueryParameters.
   * @param params PaginatedQueryParameters - The parameters to uniquely describe the search query.
   * @returns string[] - The cache-key for the search query.
   */
  search<TEntity, TFilters>(params: PaginatedQueryParameters<TEntity, TFilters> = {}): (string | PaginatedQueryParameters<TEntity, TFilters>)[] {
    return [...this.lists(), 'elasticsearch', this.getDeterministicQueryParams(params)] as const
  }

  /**
   * Returns the base cache-key for all queries that return a single resource instance.
   * You can provide an optional id to receive the base cache-key for all queries
   * that are concerned with the backend entity with the provided id.
   * Use this key if you want to invalidate the cache for single instance queries.
   * @param id number | string - Optional. If provided, the base cache-key will be specific
   *  to the provided id.
   * @returns string[] - The base cache-key for all (entity specific) detail keys
   *  of the QueryKeyFactory.
   */
  details(id?: number | string): string[] {
    if(typeof id === 'undefined') {
      // no id provided -> return the base key for *all* detail queries
      return [...this.all, 'detail'] as const
    } else {
      // received an id -> return the base key for *all entity specific* detail queries
      return [...this.all, 'detail', String(id)] as const
    }
  }

  /**
   * Returns a specific detail cache-key described by the provided instance id
   * and PaginatedQueryParameters.
   * @param id number - The id of the resource instance.
   * @param params DetailQueryParameters - The parameters to uniquely describe the detail query.
   * @returns string[] - The cache-key for the detail query.
   */
  detail<TEntity>(
    id: number | string,
    params: DetailQueryParameters<TEntity> = {}
  ): DetailQueryKey<TEntity>
  {
    // Use the id directly (as string) since details() converts it to string anyway
    return [...this.details(id), this.getDeterministicQueryParams(params)] as const
  }

  /**
   * Returns a query key for the "self" endpoint (current user).
   * This is a special case for user-related queries.
   * @returns string[] - The cache-key for the self query.
   */
  self(): string[] {
    return [...this.all, 'self'] as const
  }
}
